"use client";
import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Image from "next/image";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Controller, useForm } from "react-hook-form";
import { boolean, number, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Link from "next/link";
import { useCookies } from "react-cookie";
import { useRouter } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import LoadingButtonUI from "./LoadingButtonUI";
import moment, { Moment } from "moment";
import AllCruise from "../../app/cruise-results/result.json";
import { DateRangePicker, OrientationShape } from "react-dates";
import { CalendarInfo } from "./SearchLayout";
import ArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { EnquiryEmail, enquiryEmail } from "@/services/emailServiceV2";

type onDatesChange = { startDate: moment.Moment | null; endDate: moment.Moment | null };

interface IEnquiryForm {
  flyingFrom?: string | undefined;
  flyingTo?: string | undefined;
  departureDate?: string | undefined;
  returnDate?: string | undefined;
  destination?: string | undefined;
  name: string;
  contact: string;
  email?: string | undefined;
  message?: string | undefined;
  termsAgreed?: boolean | undefined;
  cabinType?: string | undefined;
  departureAirport?: string | undefined;
  adult?: number | undefined;
  children?: number | undefined;
  fareName?: string | undefined;
}

interface FareData {
  startsAt: string;
  endsAt: string;
  startsOn: string;
  endsOn: string;
  farePrice: string;
  cruiseReference: string;
  cruiseName: string;
  shipName: string;
  operatorName: string;
  cruiseRate: string;
  airport: string;
  fareName?: string;
  fareAvailability?: string;
  fareSetName?: string;
  cruiseNights: number;
  vacationDays?: number;
  region: string;
  cruiseType?: string | undefined;
  fareSetDealCode?: string | undefined;
  travelType?: string;
}
const EnquiryForm: React.FC<{
  open: boolean;
  onClose: () => void;
  image?: string;
  fareData?: FareData;
  isRelativeToPage?: boolean;
}> = ({ open, onClose, image, fareData, isRelativeToPage }) => {
  const schema = useMemo(
    () =>
      object().shape({
        flyingFrom: string(),
        flyingTo: string(),
        destination: string(),
        departureDate: string(),
        returnDate: string(),
        name: string().required("Name is required"),
        contact: string()
          .required("Phone number is required")
          .min(9, "Enter a valid number")
          .max(15, "Enter a valid number")
          .matches(/^[+]?[0-9]\d*$/g, "Enter a valid number"),
        email: string().email("Email address is required"),
        message: string(),
        termsAgreed: boolean().oneOf([true], "Please accept the Terms and Conditions"),
        departureAirport: string(),
        adult: number(),
        children: number(),
        cabinType: string(),
        fareName: string(),
      }),
    [],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    setValue,
    // setError,
  } = useForm<IEnquiryForm>({
    defaultValues: {
      flyingFrom: "",
      flyingTo: "",
      departureDate: "",
      returnDate: "",
      cabinType: "",
    },
    resolver: yupResolver(schema),
    mode: "all",
  });

  const lessThanMD = useMediaQuery("(max-width: 900px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  // const destinations2 = [
  //   { label: "Caribbean", id: 1 },
  //   { label: "Mediterranean", id: 2 },
  //   { label: "Alaska", id: 3 },
  //   { label: "Northern Europe", id: 4 },
  //   { label: "Asia-Pacific", id: 5 },
  //   { label: "South America", id: 6 },
  //   { label: "Australia & New Zealand", id: 7 },
  //   { label: "Hawaii", id: 8 },
  //   { label: "Antarctica", id: 9 },
  //   { label: "Middle East", id: 10 },
  //   { label: "Africa", id: 11 },
  //   { label: "Transatlantic", id: 12 },
  //   { label: "Panama Canal", id: 13 },
  //   { label: "Western Caribbean", id: 14 },
  //   { label: "Eastern Caribbean", id: 15 },
  //   { label: "Greek Isles", id: 16 },
  //   { label: "British Isles", id: 17 },
  // ];

  const destinations = useMemo(() => {
    const mapedCruise = AllCruise?.cruises.map((x, index) => {
      return { label: x?.regions[0], id: index };
    });

    const filteredRegion = mapedCruise.filter(
      (item, index, self) =>
        index === self.findIndex((uniqueLable) => uniqueLable?.label === item?.label),
    );

    return filteredRegion;
  }, [AllCruise]);

  const cabinType = ["Inside", "Outside", "Balcony", "Suite"];
  const totalPassengers = (Number(watch()?.adult) ?? 0) + (Number(watch()?.children) ?? 0);

  useEffect(() => {
    if (fareData) {
      setValue("flyingFrom", fareData?.startsAt);
      setValue("flyingTo", fareData?.endsAt);
      setValue("departureDate", moment(fareData?.startsOn).format("YYYY-MM-DD"));
      setValue("returnDate", moment(fareData?.endsOn).format("YYYY-MM-DD"));
      setValue("departureAirport", fareData?.airport);
      setSelectedDepartureDate(moment(fareData?.startsOn));
      setSelectedReturnDate(moment(fareData?.endsOn));
    }
  }, [fareData]);

  const [checked, setChecked] = useState(true);
  const acceptTerms = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const router = useRouter();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [cookies, setCookie] = useCookies(["inquiryType"]);

  const [selectedDepartureDate, setSelectedDepartureDate] = useState<Moment | null>(null);
  const [selectedReturnDate, setSelectedReturnDate] = useState<Moment | null>(null);
  const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate" | null>(null);

  const handleDatesChange = ({ startDate, endDate }: onDatesChange) => {
    setSelectedDepartureDate(startDate);
    setSelectedReturnDate(endDate);
    if (startDate) {
      setValue("departureDate", startDate.format("YYYY-MM-DD"));
    }
    if (endDate) {
      setValue("returnDate", endDate.format("YYYY-MM-DD"));
    }
  };
  // const handleremoveDates = () => {
  //   setSelectedDepartureDate(null);
  //   setValue("departureDate", undefined);
  //   setSelectedReturnDate(null);
  //   setValue("returnDate", undefined);
  // };
  const onSubmit = (data: IEnquiryForm) => {
    setIsSubmitLoading(true);
    try {
      const formData: EnquiryEmail = {
        enquiryTypes: [fareData ? 20 : 1],
        holderDetail: {
          email: data?.email ?? "",
          name: data?.name,
          phone: data?.contact ?? "-",
        },
        sendEmail: true,
        ...(!fareData && {
          departureDate: moment(data?.departureDate ?? "").format("DD/MM/YYYY"),
          returnDate: moment(data?.returnDate ?? "").format("DD/MM/YYYY"),
          rooms: [
            {
              adult: data?.adult,
              children: data?.children,
            },
          ],
        }),
        cruises:
          fareData && data
            ? [
                {
                  startAt: data?.flyingFrom ?? "",
                  startDate: moment(data?.departureDate ?? "").format("DD/MM/YYYY") ?? "",
                  endsAt: data?.flyingTo ?? "",
                  endDate: moment(data?.returnDate ?? "").format("DD/MM/YYYY") ?? "",
                  adultCount: data?.adult,
                  childCount: data?.children,
                  fareGradeName: data?.fareName,
                  adultPassengers: [
                    {
                      title: "",
                      firstName: data?.name,
                      gender: "",
                      dateOfBirth: "",
                      type: "",
                    },
                  ],
                  cruiseReference: fareData?.cruiseReference ?? "",
                  cruiseName: fareData?.cruiseName ?? "",
                  shipName: fareData?.shipName ?? "",
                  operatorName: fareData?.operatorName ?? "",
                  cruiseRate: fareData?.cruiseRate ?? "",
                  fareAvailability: fareData?.fareAvailability ?? "",
                  cruiseNights: fareData?.cruiseNights ?? 0,
                  vacationDays: fareData?.vacationDays ?? 0,
                  cruiseTypes: fareData?.cruiseType ? [fareData.cruiseType] : [],
                  regions: fareData?.region ? [fareData?.region] : [],
                  travelType: fareData?.travelType ?? "",
                  totalCharge: fareData?.farePrice ?? "",
                  fareSetName: fareData?.fareSetName,
                  fareSetDealCode: fareData?.fareSetDealCode,
                },
              ]
            : [],
        pageUrl: window.location.href.split("?")[0],
        message: data?.message,
        personCount: totalPassengers,
        cabinClass: data?.cabinType ?? "",
        ...(data.destination && {
          destinationWithNights: [
            {
              destination: data.destination,
            },
          ],
        }),
      };

      /*
{
        siteCode: siteCode ?? "",
        email: data?.email ?? "",
        isSaveEnquiry: true,
        data: {
          name: data?.name,
          email: data?.email ?? "",
          phone: data?.contact ?? "-",
          inquiryType: fareData ? "cruise" : "common",
          departureDate: moment(data?.departureDate ?? "").format("DD/MM/YYYY"),
          returnDate: moment(data?.returnDate ?? "").format("DD/MM/YYYY"),
          destination: data?.destination,
          ...(fareData && {
            flyingFrom: data?.flyingFrom,
            flyingTo: data?.flyingTo,
            price: fareData?.farePrice,
            cruise: {
              cruiseReference: fareData?.cruiseReference,
              cruiseName: fareData?.cruiseName,
              shipName: fareData?.shipName,
              operatorName: fareData?.operatorName,
              cruiseRate: fareData?.cruiseRate,
              departureAirport: fareData?.airport,
              fareSetName: fareData?.fareSetName,
              fareSetDealCode: fareData?.fareSetDealCode,
              fareGradeName: data?.fareName,
              fareAvailability: fareData?.fareAvailability,
              cruiseNights: fareData?.cruiseNights,
              vacationDays: fareData?.vacationDays,
              cruiseType: fareData?.cruiseType,
              region: fareData?.region,
            },
          }),
          adultCount: data?.adult,
          childCount: data?.children,
          personCount: totalPassengers,
          url: window.location.href.split("?")[0],
          message: data?.message,
          adultPassenger: {
            title: "",
            firstName: data?.name,
          },
          enquiryTime: `${new Date()}`,
          cabinType: data?.cabinType ?? "",
        },
      };
*/
      enquiryEmail(formData)
        .then(() => {
          setCookie("inquiryType", formData.enquiryTypes?.[0] ?? cookies?.inquiryType ?? 1, {
            expires: new Date(Date.now() + 60 * 3000),
            path: "/",
          });
          router.push("/thank-you");
          reset();
        })
        .catch(() => {
          enqueueSnackbar("Some thing went wrong. Please try again in a while", {
            variant: "error",
          });
        })
        .finally(() => setIsSubmitLoading(false));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      {focusedInput && !lessThanMD && (
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: "unset",
            bgcolor: "rgb(0 0 0 / 23%)",
            overflow: "hidden",
            width: "100%",
            height: "100%",
            top: "0px",
            left: "0px",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgb(0 0 0 / 41%)",
            transition: "all 0.3s ease",
          }}
          open={open}
        ></Backdrop>
      )}
      <Dialog
        open={open}
        onClose={onClose}
        id="enquiryModal"
        fullWidth={true}
        className={`Mui_dialog_border_radius modal_bg_blur`}
        fullScreen={lessThanMD}
        maxWidth="md"
        // maxWidth="lg"
        disablePortal={isRelativeToPage}
        disableScrollLock={isRelativeToPage}
        hideBackdrop={isRelativeToPage}
        disableAutoFocus={isRelativeToPage}
        disableRestoreFocus={isRelativeToPage}
        disableEnforceFocus={isRelativeToPage}
        sx={{
          zIndex: 1300,
          backdropFilter: "blur(5px)",
          borderRadius: { xs: "0px", md: "10px" },
          ".Mui-error": { color: "red" },
          ".MuiDialog-paper": {
            overflowY: { xs: "auto", lg: "auto" },
            position: "relative",
            borderRadius: { xs: "0px !important", md: "15px !important" },
          },
          ...(isRelativeToPage && {
            position: "relative",
            zIndex: "unset",
            ".MuiPaper-root.MuiDialog-paper": {
              m: { xs: "0px", md: "0rem" },
              mt: { xs: "0px", md: "1rem" },
              boxShadow: {
                xs: "none",
                md: focusedInput ? "none" : "rgb(0 0 0 / 8%) 0px 2px 15px;",
              },
              border: { xs: "none", md: focusedInput ? "none" : "1px solid #ccc" },
              borderRadius: { xs: "0px", md: "15px" },
              width: "100%",
            },
          }),
        }}
      >
        {focusedInput && !lessThanMD && (
          <Box
            onClick={() => setFocusedInput(null)}
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              top: "0px",
              left: "0px",
              backdropFilter: "blur(5px)",
              backgroundColor: "#4646468c",
              zIndex: 2,
              transition: "all 0.3s ease",
            }}
          />
        )}
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 12, lg: 4 }} display={isRelativeToPage ? "none" : "block"}>
            {/* <Grid size={{ xs: 12, md: 4 }}> */}
            <Box
              sx={{
                // height: { xs: '200px', md: '200px', lg: '100%' },
                minHeight: { xs: "200px", md: "200px", lg: "100%" },
                // minHeight: { xs: '200px', md: '100%' },
                width: "100%",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {fareData ? (
                <Image
                  src={
                    image ??
                    "https://widgety-assets.s3.amazonaws.com/2020/10/20/08/08/28/006b620c-4214-4dd0-8fa8-75ec682e9eef/image coming soon image not available.jpg"
                  }
                  alt="EnquiryForm"
                  fill
                  style={{
                    objectPosition: "center",
                    objectFit: "cover",
                  }}
                  unoptimized
                  loader={({ src }) => src}
                />
              ) : (
                <Image
                  src={"/images/destination-images/caribbean-cruises.jpg"}
                  alt="EnquiryForm"
                  fill
                  style={{
                    objectPosition: "center",
                    objectFit: "cover",
                  }}
                  unoptimized
                  loader={({ src }) => src}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: "1.7rem 0rem 1.5rem 0rem",
                }}
              >
                <Image
                  src={"/images/others/vguk-logo.png"}
                  alt="VGUK Logo"
                  width={100}
                  height={100}
                  style={{
                    objectPosition: "center",
                    objectFit: "contain",
                    zIndex: 1,
                    filter: "drop-shadow(black 0px 2px 4px)",
                  }}
                />
                <Typography
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    textAlign: "center",
                    fontWeight: 500,
                    fontSize: { xs: "1.2rem", md: "1.8rem" },
                    textShadow: "3px -2px 25px black, -3px 2px 25px black",
                    color: "#fff",
                    p: "0rem 1.5rem 0rem 1.5rem",
                  }}
                >
                  {fareData?.cruiseName
                    ? fareData?.cruiseName
                    : "Embark on Your Dream Cruise Journey!"}
                </Typography>

                {fareData && (
                  <Typography
                    sx={{
                      fontFamily: "'blauerNue', 'blauerNue Fallback'",
                      position: "relative",
                      zIndex: 1,
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: "1.5rem",
                      textShadow: "3px -2px 25px black, -3px 2px 25px black",
                      color: "#fff",
                      p: "0.5rem 1.5rem 0rem 1.5rem",
                    }}
                  >
                    £ {fareData?.farePrice} pp
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: isRelativeToPage ? 12 : 8 }}>
            {/* <Grid size={{ xs: 12, md: 8 }}> */}
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{
                input: {
                  height: "14px",
                },
                label: {
                  top: "-6px",
                },
                ".MuiInputLabel-shrink": {
                  top: "1px",
                },
                ".MuiOutlinedInput-root": {
                  height: "42px",
                },
              }}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{
                  p: {
                    xs: isRelativeToPage ? "1rem 0rem 0rem 0rem" : "1rem 1rem 0rem 1rem",
                    md: "1rem 1rem 0rem 1rem",
                  },
                }}
              >
                {!fareData ? (
                  <>
                    <SectionTitle
                      title="Cruise Journey!"
                      description="Set Sail for an Unforgettable Adventure!"
                      sx={{
                        mb: "14px",
                      }}
                    />
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 7, md: 6 }}>
                        <Autocomplete
                          disablePortal
                          options={destinations}
                          fullWidth
                          renderInput={(params) => (
                            <TextField
                              {...register("destination")}
                              label="Destination"
                              placeholder="Select your dream destination"
                              variant="outlined"
                              {...params}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: fareData ? 6 : 5, md: fareData ? 6 : 6 }}>
                        <Controller
                          control={control}
                          name="departureDate"
                          render={({ field }) => (
                            <Box
                              sx={{
                                ".DateRangePicker": {
                                  width: "100%",
                                  ".DateRangePickerInput": {
                                    display: "grid !important",
                                    gridTemplateColumns: "1fr 1fr",
                                    bgcolor: "#fff",
                                    height: "100%",
                                    gap: "12px !important",
                                    ".DateInput": {
                                      background: "#fff",
                                      width: { xs: "100% !important", md: "100% !important" },
                                      height: "100% !important",
                                    },
                                    ".DateInput > input": {
                                      height: "100% !important",
                                      bgcolor: "#fff",
                                      border: "1px solid #c4c4c4",
                                      borderRadius: "8px",
                                      padding: "10.5px",
                                      width: "100%",
                                      "&:hover": {
                                        border: "1px solid #000",
                                      },
                                    },

                                    ".DateRangePickerInput_arrow": {
                                      display: "none !important",
                                    },
                                  },
                                },
                                ".DateRangePickerInput_clearDates": {
                                  display: "none",
                                },

                                ".DateInput_input": {
                                  color: "#111",
                                  textAlign: "left",
                                  p: "2px 0px",
                                  pt: "4px",
                                  fontFamily: "inherit",
                                  fontSize: "16px !important",
                                  fontWeight: "400 !important",
                                  "&::placeholder": {
                                    fontWeight: 300,
                                    color: "#6c6c6c",
                                    textAlign: "left",
                                  },
                                },

                                ".DateInput_input__disabled": {
                                  fontStyle: "initial",
                                },
                                ".DateInput_input__focused": {
                                  borderBottom: "none !important",
                                },

                                //Calander
                                ".DateInput_fang": {
                                  display: "none",
                                },
                                ".DateRangePicker_picker__directionLeft_2": {
                                  position: "fixed",
                                  top: "50% !important",
                                  left: "50% !important",
                                  transform: "translate(-50%, -50%) !important",
                                  padding: "16px 16px 0px 16px",
                                  borderRadius: "16px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,.05),0 0 0 1px rgba(0,0,0,.07)",
                                  overflow: "hidden",
                                  zIndex: 3,
                                },
                                ".DayPicker ": {
                                  width: "800px !important",
                                },
                                ".DayPicker__withBorder": {
                                  boxShadow: "none",
                                },
                                ".DayPicker_weekHeaders": {
                                  ml: "6px",
                                  "div:last-of-type": {
                                    right: "26px",
                                    left: "unset !important",
                                  },
                                },
                                ".DayPickerNavigation": {
                                  display: "flex",
                                  justifyContent: "space-between",
                                  "div:last-of-type": {
                                    position: "relative",
                                    right: "42px",
                                  },
                                },

                                ".CalendarMonthGrid": {
                                  left: "0px",
                                },
                                ".CalendarMonth_table": {
                                  borderCollapse: "unset",
                                  borderSpacing: "1px",
                                },
                                ".CalendarMonth_1": {
                                  p: "0px 16px 0px 0px !important",
                                },
                                ".DayPicker_weekHeader_1": {
                                  p: "0px 16px 0px 0px !important",
                                },
                              }}
                            >
                              <DateRangePicker
                                {...field}
                                startDate={selectedDepartureDate}
                                startDateId="start-date"
                                endDate={selectedReturnDate}
                                endDateId="end-date"
                                onDatesChange={handleDatesChange}
                                focusedInput={focusedInput}
                                onFocusChange={(input: "startDate" | "endDate" | null) =>
                                  setFocusedInput(input)
                                }
                                noBorder
                                isOutsideRange={() => false}
                                displayFormat="DD/MM/YYYY"
                                showClearDates
                                reopenPickerOnClearDates
                                startDatePlaceholderText="Explore from"
                                endDatePlaceholderText="Explore until"
                                hideKeyboardShortcutsPanel
                                firstDayOfWeek={1}
                                orientation={
                                  !isMobile
                                    ? ("horizontal" as OrientationShape)
                                    : ("verticalScrollable" as OrientationShape)
                                }
                                calendarInfoPosition="bottom"
                                numberOfMonths={lessThanMD ? 25 : 2}
                                withPortal={lessThanMD}
                                readOnly={lessThanMD}
                                withFullScreenPortal={lessThanMD}
                                daySize={lessThanMD ? 50 : 55}
                                navNext={
                                  lessThanMD ? null : (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disableElevation
                                      sx={{
                                        // m: "14px",
                                        p: "3px",
                                        borderRadius: "8px",
                                        minWidth: "unset",
                                        zIndex: 1,
                                        bgcolor: "#e30053",
                                        color: "#fff",
                                        border: "1px solid",
                                        borderColor: "#fff",
                                        boxShadow: "0px 0px 7px 2px rgb(0 0 0 / 19%)",
                                        "&:hover": {
                                          bgcolor: "#fff",
                                          color: "#e30053",
                                          borderColor: "#e30053",
                                        },
                                        svg: {
                                          fontSize: "1.5rem",
                                        },
                                      }}
                                    >
                                      <ArrowRightIcon />
                                    </Button>
                                  )
                                }
                                navPrev={
                                  lessThanMD ? null : (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disableElevation
                                      sx={{
                                        // m: "14px",
                                        p: "3px",
                                        borderRadius: "8px",
                                        minWidth: "unset",
                                        zIndex: 1,
                                        bgcolor: "#e30053",
                                        color: "#fff",
                                        border: "1px solid",
                                        borderColor: "#fff",
                                        boxShadow: "0px 0px 7px 2px rgb(0 0 0 / 19%)",
                                        "&:hover": {
                                          bgcolor: "#fff",
                                          color: "#e30053",
                                          borderColor: "#e30053",
                                        },
                                        svg: {
                                          fontSize: "1.5rem",
                                        },
                                      }}
                                    >
                                      <ArrowLeftIcon />
                                    </Button>
                                  )
                                }
                                renderDayContents={(day) => (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      justifyContent: "flex-end",
                                      alignItems: "flex-end",
                                      p: "6px",
                                      fontSize: "1.1rem",
                                      borderRadius: "8px",
                                      border: "1px solid #ddd",
                                    }}
                                  >
                                    {moment(day).format("D")}
                                  </Box>
                                )}
                                renderCalendarInfo={() => (
                                  <CalendarInfo
                                    applyHandler={() => setFocusedInput(null)}
                                    clearDatesHandler={() => {
                                      setFocusedInput("startDate");
                                      handleDatesChange({ startDate: null, endDate: null });
                                    }}
                                    departureDate={watch()?.departureDate ?? ""}
                                    retureDate={watch()?.returnDate}
                                  />
                                )}
                              />
                            </Box>
                          )}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Controller
                          control={control}
                          name={"cabinType"}
                          render={({ field, fieldState: { invalid } }) => (
                            <>
                              <FormControl fullWidth>
                                <InputLabel>Cabin Choice</InputLabel>
                                <Select {...field} label="Cabin Choice" error={invalid}>
                                  {cabinType.map((list, index) => (
                                    <MenuItem key={index} value={list}>
                                      {list}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </>
                          )}
                        />
                      </Grid>
                      {/* ------------------- # Adult ------------------- */}
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <TextField
                          fullWidth
                          {...register("adult", {
                            max: 9 - Number(watch().children),
                            min: 0,
                          })}
                          label="Adult"
                          type="number"
                          color="secondary"
                          inputProps={{ min: 0, max: 9 - Number(watch().children) }}
                        />
                      </Grid>
                      {/* ------------------- # Children ------------------- */}
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <TextField
                          fullWidth
                          {...register("children", {
                            max: 9 - Number(watch().adult),
                            min: 0,
                          })}
                          label="Children"
                          type="number"
                          color="secondary"
                          inputProps={{ min: 0, max: 9 - Number(watch().adult) }}
                        />
                      </Grid>

                      {totalPassengers == 9 && (
                        <Grid size={{ xs: 12 }}>
                          <Box
                            sx={{
                              color: "#d32f2f",
                              bgcolor: "#ff000020",
                              width: "fit-content",
                              p: "4px 10px",
                              fontSize: "14px",
                              borderRadius: "8px",
                              mt: "0px",
                            }}
                          >
                            Maximum no of travellers is 9.
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "'blauerNue', 'blauerNue Fallback'",
                      span: { fontWeight: 700, color: "primary" },
                      mr: { lg: "48px" },
                    }}
                  >
                    Set sail on an extraordinary adventure with <span>{fareData?.cruiseName}</span>!
                    Departing from <span>{fareData?.startsAt}</span> on{" "}
                    <span>{moment(fareData?.startsOn).format("DD/MM/YYYY")}</span> , and arriving in{" "}
                    <span>{fareData?.endsAt}</span> on{" "}
                    <span>{moment(fareData?.endsOn).format("DD/MM/YYYY")}</span> —where every moment
                    is filled with luxury, excitement, and breathtaking views. Book your dream
                    cruise today!
                  </Typography>
                )}
              </Grid>
              {/* } */}
              {fareData && (
                <Grid
                  size={{ xs: 12 }}
                  sx={{
                    p: {
                      xs: isRelativeToPage ? "1rem 0rem 0rem 0rem" : "1rem 1rem 0rem 1rem",
                      md: "1rem 1rem 0rem 1rem",
                    },
                  }}
                >
                  <SectionTitle
                    title="Traveller Preferences"
                    description="We'll contact you back soon"
                    sx={{ mb: "14px" }}
                  />

                  <Grid container spacing={1.5}>
                    {fareData.fareName && (
                      <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Controller
                          control={control}
                          name={"fareName"}
                          render={({ field, fieldState: { invalid } }) => (
                            <>
                              <FormControl fullWidth>
                                <InputLabel>Fare Name</InputLabel>
                                <Select {...field} label="Fare Name" error={invalid}>
                                  <MenuItem value={fareData?.fareName}>
                                    {fareData?.fareName}
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </>
                          )}
                        />
                      </Grid>
                    )}

                    <Grid
                      size={{ xs: 6, sm: fareData.fareName ? 3 : 4, md: fareData.fareName ? 3 : 4 }}
                    >
                      <TextField
                        fullWidth
                        {...register("adult", {
                          max: 9 - Number(watch().children),
                          min: 0,
                        })}
                        label="Adult"
                        type="number"
                        color="secondary"
                        inputProps={{ min: 0, max: 9 - Number(watch().children) }}
                      />
                    </Grid>
                    <Grid
                      size={{ xs: 6, sm: fareData.fareName ? 3 : 4, md: fareData.fareName ? 3 : 4 }}
                    >
                      <TextField
                        fullWidth
                        {...register("children", {
                          max: 9 - Number(watch().adult),
                          min: 0,
                        })}
                        label="Children"
                        type="number"
                        color="secondary"
                        inputProps={{ min: 0, max: 9 - Number(watch().adult) }}
                      />
                    </Grid>

                    {totalPassengers == 9 && (
                      <Grid size={{ xs: 12 }}>
                        <Box
                          sx={{
                            color: "#d32f2f",
                            bgcolor: "#ff000020",
                            width: "fit-content",
                            p: "4px 10px",
                            fontSize: "14px",
                            borderRadius: "8px",
                            mt: "0px",
                          }}
                        >
                          Maximum no of travellers is 9.
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              <Grid
                size={{ xs: 12 }}
                sx={{
                  p: {
                    xs: isRelativeToPage ? "1rem 0rem 0rem 0rem" : "16px 16px 60px 16px",
                    md: "1rem 1rem 0rem 1rem",
                  },
                }}
              >
                <SectionTitle
                  title="Contact Details"
                  description="We'll contact you back soon"
                  sx={{ mb: "14px" }}
                />

                <Grid container spacing={1.5}>
                  {/* ------------------- # Name ------------------- */}
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <TextField
                      {...register("name")}
                      label="Name"
                      placeholder="Enter your name here"
                      type="text"
                      fullWidth
                      error={errors?.name ? true : false}
                      helperText={errors?.name?.message}
                    />
                  </Grid>

                  {/* ------------------- # Phone number ------------------- */}
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <TextField
                      fullWidth
                      {...register("contact")}
                      label="Phone Number"
                      color="secondary"
                      error={errors?.contact ? true : false}
                      helperText={errors?.contact?.message}
                    />
                  </Grid>
                  {/* ------------------- # Email Address ------------------- */}
                  <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                    <TextField
                      fullWidth
                      {...register("email")}
                      label="Email"
                      color="secondary"
                      error={errors?.email ? true : false}
                      helperText={errors?.email?.message}
                    />
                  </Grid>

                  {/* ------------------- # Message ------------------- */}
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      {...register("message")}
                      fullWidth
                      multiline
                      rows={3}
                      label="Special Request"
                      placeholder="Write a message for special request"
                      color="secondary"
                      error={!!errors?.message}
                      helperText={errors?.message?.message}
                      sx={{
                        ".MuiOutlinedInput-input.MuiInputBase-input": {
                          p: "0",
                          pt: "0",
                        },
                        ".MuiOutlinedInput-root": {
                          height: "unset",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                size={{ xs: 12 }}
                sx={{ p: { xs: isRelativeToPage ? "1rem 0rem" : "1rem", md: "1rem" } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  {!isMobile && (
                    <Box>
                      <Typography variant="body2">
                        We take all the necessary steps to protect your data. For more details refer
                        to our{" "}
                        <Link href="/privacy-policy" passHref target="_blank">
                          <span
                            style={{
                              textDecoration: "none",
                              color: "primary.main",
                            }}
                          >
                            privacy policy
                          </span>
                        </Link>{" "}
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            {...register("termsAgreed")}
                            checked={checked}
                            onChange={acceptTerms}
                            sx={{
                              "&.Mui-checked": {
                                color: "primary.main",
                              },
                              mr: "-2px",
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2">
                            I agree to the{" "}
                            <Link
                              href="/help-and-support#terms-and-conditions"
                              passHref
                              target="_blank"
                            >
                              <span
                                style={{
                                  textDecoration: "none",
                                  color: "primary.main",
                                }}
                              >
                                terms and conditions
                              </span>
                            </Link>{" "}
                          </Typography>
                        }
                        sx={{ mt: "-5px", mb: "-10px" }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                      width: { xs: "100%", sm: "fit-content" },
                      position: { xs: isRelativeToPage ? "relative" : "fixed", sm: "relative" },
                      bottom: { xs: isRelativeToPage ? "unset" : "0px", sm: "unset" },
                      left: { xs: isRelativeToPage ? "unset" : "0px", sm: "unset" },
                      padding: { xs: isRelativeToPage ? "0rem" : "1rem", sm: "0px" },
                      bgcolor: {
                        xs: "#fff",
                        sm: "unset",
                      },
                      boxShadow: {
                        xs: isRelativeToPage ? "unset" : "0px -4px 10px rgba(0,0,0,0.15)",
                        sm: "none",
                      },
                      zIndex: { xs: 1400, sm: "unset" },
                    }}
                  >
                    {isMobile && (
                      <Box sx={{ display: { xs: "block", sm: "none" } }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...register("termsAgreed")}
                              checked={checked}
                              onChange={acceptTerms}
                              sx={{
                                "&.Mui-checked": {
                                  color: "primary.main",
                                },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ mt: "5px" }}>
                              I agree to the{" "}
                              <Link
                                href="/help-and-support#terms-and-conditions"
                                passHref
                                target="_blank"
                              >
                                <span
                                  style={{
                                    textDecoration: "none",
                                    color: "primary.main",
                                  }}
                                >
                                  T&C
                                </span>
                              </Link>{" "}
                              of World Cruise Vibes
                            </Typography>
                          }
                          sx={{ mt: "-4px" }}
                        />
                      </Box>
                    )}

                    <LoadingButtonUI isSubmitLoading={isSubmitLoading} />
                  </Box>
                </Box>

                {errors?.termsAgreed && (
                  <FormHelperText
                    error
                    sx={{
                      color: "red !important",
                      position: "relative",
                      left: { xs: "30px", md: "0px" },
                      top: { xs: "-14px", md: "-10px" },
                      width: "fit-content",
                    }}
                  >
                    {errors?.termsAgreed?.message}
                  </FormHelperText>
                )}
              </Grid>
            </Box>
          </Grid>
        </Grid>
        {!isRelativeToPage && (
          <Button
            disableElevation
            sx={{
              minWidth: "unset",
              position: "absolute",
              top: "16px",
              right: "16px",
              zIndex: 1,
              p: "5px",
              borderRadius: "12px",
              bgcolor: "#060273",
              border: "1px solid #fff",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
              color: "#fff",
              "&:hover": {
                bgcolor: "#fff",
                color: "#060273",
                border: "1px solid #060273",
              },
            }}
            onClick={onClose}
            variant="contained"
          >
            <CloseRoundedIcon sx={{ width: "1.5rem", height: "1.5rem" }} />
          </Button>
        )}
      </Dialog>
    </>
  );
};

export default EnquiryForm;

export const SectionTitle: React.FC<{
  title: string;
  description: string;
  sx?: SxProps<Theme>;
}> = ({ title, description, sx }) => {
  return (
    <Box sx={{ ...sx }}>
      <Typography
        sx={{
          display: "flex",
          color: "primary.main",
          fontWeight: 700,
        }}
        variant="subtitle2"
      >
        <VerifiedIcon sx={{ mr: "5px" }} />

        <Box>
          <Box
            sx={{
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "15px",
            }}
          >
            {title}
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: "-2px",
              fontSize: "14px",
              textTransform: "initial",
              color: "#777",
            }}
          >
            {description}
          </Typography>
        </Box>
      </Typography>
    </Box>
  );
};

export const TravellerTypeCounter: React.FC<{
  title: string;
  sub: string;
  value: number;
  increaseItem(): void;
  decreaseItem(): void;
  isHotelEnquiry?: boolean;
}> = ({ sub, title, value, increaseItem, decreaseItem, isHotelEnquiry }) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: "12px",
          display: { xs: "block", sm: "flex" },
          alignItems: "flex-end",
          gap: { xs: "0px", sm: "5px" },
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: 1.4,
            mb: isHotelEnquiry ? "0px" : "-2px",
          }}
        >
          {title}
        </Typography>
        <Typography
          component={"span"}
          variant="body1"
          sx={{ fontSize: "0.8rem", color: "#999", lineHeight: 1.4 }}
        >
          {sub}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={decreaseItem}
          variant="outlined"
          sx={{
            minWidth: "unset",
            borderRadius: "20%",
            p: "3px",
            border: "1px solid #00A3FF",
            bgcolor: "#D0F4FF55",
            color: "#00A3FF",
            "&:hover": {
              border: "1px solid #00A3FF",
              bgcolor: "#D0F4FF",
              color: "#00A3FF",
            },
          }}
        >
          <RemoveRoundedIcon />
        </Button>
        <Typography sx={{ minWidth: "30px", textAlign: "center" }}>{value}</Typography>
        <Button
          onClick={increaseItem}
          variant="outlined"
          sx={{
            minWidth: "unset",
            borderRadius: "20%",
            p: "3px",
            border: "1px solid #00A3FF",
            bgcolor: "#D0F4FF55",
            color: "#00A3FF",
            "&:hover": {
              border: "1px solid #00A3FF",
              bgcolor: "#D0F4FF",
              color: "#00A3FF",
            },
          }}
        >
          <AddRoundedIcon />
        </Button>
      </Box>
    </Box>
  );
};
